import {
  ContentBlockKind,
  CopilotConversation,
  CopilotConversationContentBlock,
} from 'models/copilot/copilotConversation';
import { CopilotDocumentIngestPayload } from 'models/copilot/copilotDocumentIngest';
import { ResourceListType } from 'models/resourceList';

export type DocumentViewMode = 'new' | 'preview' | 'docx' | 'page';

export const DocumentMimeTypes = {
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  pdf: 'application/pdf',
  sharePointPage: 'text/html',
  markdown: 'text/markdown',
} as const;

// The ingest payload the backend stores on an uploaded/library document block (camelCase over the wire). Used to
// tell a transient uploaded file (held in session temp storage) apart from agent-created markdown.
export const getCopilotDocumentPayload = (
  block: CopilotConversationContentBlock | undefined,
): CopilotDocumentIngestPayload | undefined => {
  const payload = block?.payload;
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return undefined;

  return payload as CopilotDocumentIngestPayload;
};

// A transient uploaded file: streamed into session temp storage (it has a tempBlobName) and not yet saved to the
// library. Its preview must render the original file, never the markdown extracted for the agent. This mirrors the
// backend signal (CopilotDocumentContentService keys off payload.TempBlobName).
export const isTransientUploadBlock = (block: CopilotConversationContentBlock | undefined): boolean => {
  if (!block || block.savedLibraryLinkId) return false;

  return !!getCopilotDocumentPayload(block)?.tempBlobName;
};

export const getDocumentViewMode = (block: CopilotConversationContentBlock | undefined): DocumentViewMode => {
  if (!block) return 'new';

  const mime = block.mimeType ?? 'text/markdown';

  // A saved library document opens its editor mode: the docx editor for a Word file, the page renderer for a site
  // page. Other saved types (e.g. an uploaded spreadsheet/PDF) fall through to the read-only preview below.
  if (block.savedLibraryLinkId) {
    if (mime === DocumentMimeTypes.docx) {
      return 'docx';
    }

    if (mime === DocumentMimeTypes.sharePointPage) {
      return 'page';
    }
  }

  // Uploaded-but-unsaved files (any extractor type) and PDFs get a native read-only preview of the original file.
  // Agent-created markdown has no upload payload and falls through to the inline markdown render.
  if (isTransientUploadBlock(block) || mime === DocumentMimeTypes.pdf) {
    return 'preview';
  }

  return 'new';
};

// Saving a markdown document to the library converts it by target type (see CopilotDocumentLibrarySaveService):
// a document library produces a Word file (docx), a site-page library produces an HTML page. The backend re-types
// the content block accordingly; mirror that mime on the client so a just-saved document immediately switches to
// its editor mode (Edit button / docx|page mode) instead of staying a read-only preview until a reload.
export const getLibrarySavedMimeType = (listType: ResourceListType | undefined): string | undefined => {
  switch (listType) {
    case ResourceListType.DocumentLibrary:
      return DocumentMimeTypes.docx;
    case ResourceListType.SitePageLibrary:
      return DocumentMimeTypes.sharePointPage;
    default:
      return undefined;
  }
};

export const hasPreviewableDocumentBlock = (block: CopilotConversationContentBlock | undefined): boolean => {
  if (block?.kind !== ContentBlockKind.Document || !block.id) return false;
  if (block.savedLibraryLinkId) return true;
  if (isTransientUploadBlock(block)) return true;

  return (block.text?.trim().length ?? 0) > 0;
};

export const getDocumentContentBlocks = (
  conversation: CopilotConversation | undefined,
): CopilotConversationContentBlock[] => {
  if (!conversation?.thread) return [];

  return conversation.thread.flatMap((item) => item.contentBlocks?.filter(hasPreviewableDocumentBlock) ?? []);
};

export interface CopilotDocumentBlockFamily {
  id: string;
  versions: CopilotConversationContentBlock[];
  latest: CopilotConversationContentBlock;
}

export const getDocumentVersionKey = (block: CopilotConversationContentBlock | undefined): string => {
  if (!block) return '';

  return `${block.id ?? 'document'}:${block.version ?? 0}:${block.created?.toISOString() ?? ''}`;
};

export const getDocumentBlockFamilies = (
  conversation: CopilotConversation | undefined,
): CopilotDocumentBlockFamily[] => {
  const groups = new Map<string, CopilotConversationContentBlock[]>();

  getDocumentContentBlocks(conversation).forEach((block) => {
    if (!block.id) return;

    const versions = groups.get(block.id) ?? [];
    versions.push(block);
    groups.set(block.id, versions);
  });

  return Array.from(groups.entries()).map(([id, versions]) => {
    const sortedVersions = [...versions].sort((left, right) => {
      const createdDiff = (right.created?.getTime() ?? 0) - (left.created?.getTime() ?? 0);
      if (createdDiff !== 0) return createdDiff;

      return (right.version ?? 0) - (left.version ?? 0);
    });

    return {
      id,
      versions: sortedVersions,
      latest: sortedVersions[0],
    };
  });
};

export const getConversationWithoutDocumentBlocks = (
  conversation: CopilotConversation | undefined,
): CopilotConversation | undefined => {
  if (!conversation) return undefined;

  const clone = conversation.clone();
  clone.thread = clone.thread.map((item) => {
    const itemClone = item.clone();
    itemClone.contentBlocks = itemClone.contentBlocks?.filter((block) => block.kind !== ContentBlockKind.Document);

    return itemClone;
  });

  return clone;
};
